/**
 * Linking a prospect to ESPN from the app.
 *
 * Until Sep 2026 the only writer of a prospect's espn_id was the nightly
 * roster job, which needs an exact name on a resolved college roster. Three
 * other paths found the id and dropped it. This is the one place the app
 * links: on create, on an edit that changes school or league, on Refresh
 * Data, and by hand from an ESPN URL.
 *
 * Nothing is guessed. The scraper's lookup is accepted only when the club it
 * returns is the school on record (or the record has no school yet), and an
 * existing id is never overwritten by a search; only a person can replace it.
 */

import { pb } from '../lib/pocketbase'
import { lookupAthlete } from './headshotService'
import { isEspnCollegeLeague, isNflLeague } from './leagueHelpers'

export type LinkableProspect = {
  id: string
  full_name: string
  sport?: string | null
  league?: string | null
  school?: string | null
  headshot_url?: string | null
  external_ids?: Record<string, any> | null
}

export type LinkOutcome =
  | { linked: true; espn_id: string; team: string | null; headshot_url: string | null }
  | { linked: false; reason: 'already_linked' | 'not_indexed' | 'not_found' | 'club_differs' | 'no_name'; detail?: string }

/** The id inside an ESPN player URL, or a bare id. Null when neither. */
export function parseEspnId(input: string): string | null {
  const s = input.trim()
  if (/^\d{3,}$/.test(s)) return s
  const m = /\/id\/(\d+)/.exec(s)
  return m ? m[1] : null
}

/** The same two-way substring rule the MCP linker uses for club names. */
export function clubMatches(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = String(a ?? '').trim().toLowerCase()
  const y = String(b ?? '').trim().toLowerCase()
  if (!x || !y) return false
  return x === y || x.includes(y) || y.includes(x)
}

export function espnIdOf(p: { external_ids?: Record<string, any> | null }): string | null {
  const id = p.external_ids?.espn_id
  return id != null && String(id).trim() !== '' ? String(id) : null
}

/** Does ESPN index this prospect's league at all? */
export function espnIndexes(p: LinkableProspect): boolean {
  return isEspnCollegeLeague(p.league) || isNflLeague(p.league)
}

async function verifiedImage(url: string | null): Promise<string | null> {
  if (!url) return null
  try {
    const r = await fetch(url, { method: 'GET', signal: AbortSignal.timeout(8000) })
    return r.ok && (r.headers.get('content-type') ?? '').startsWith('image/') ? url : null
  } catch {
    return null
  }
}

/**
 * Look the prospect up through the scraper and, when the answer is
 * unambiguous, write external_ids.espn_id (plus the headshot when the record
 * has none). Returns what happened so the page can say it.
 */
export async function linkProspectToEspn(p: LinkableProspect): Promise<LinkOutcome> {
  if (espnIdOf(p)) return { linked: false, reason: 'already_linked' }
  if (!p.full_name?.trim()) return { linked: false, reason: 'no_name' }
  if (!espnIndexes(p)) return { linked: false, reason: 'not_indexed', detail: p.league ?? '' }

  const found = await lookupAthlete(p.full_name, p.sport, p.league)
  if (!found.espn_id) return { linked: false, reason: 'not_found' }
  if (p.school && found.team && !clubMatches(p.school, found.team)) {
    return { linked: false, reason: 'club_differs', detail: `ESPN lists ${found.team}; the record says ${p.school}` }
  }

  const headshot = p.headshot_url ? null : await verifiedImage(found.headshot_url)
  const external_ids = {
    ...(p.external_ids ?? {}),
    espn_id: found.espn_id,
    espn_matched_by: 'scraper-search',
    espn_matched_at: new Date().toISOString(),
  }
  await pb.collection('prospects').update(p.id, {
    external_ids,
    ...(headshot ? { headshot_url: headshot } : {}),
    ...(p.school || !found.team ? {} : { school: found.team }),
  })
  return { linked: true, espn_id: found.espn_id, team: found.team, headshot_url: headshot }
}

/** A person pasted the ESPN page: that id wins, recorded as confirmed by them. */
export async function confirmProspectEspnId(p: LinkableProspect, input: string): Promise<string> {
  const espnId = parseEspnId(input)
  if (!espnId) throw new Error('Paste the ESPN player page URL (it contains /id/12345/) or the id itself.')
  const external_ids = {
    ...(p.external_ids ?? {}),
    espn_id: espnId,
    espn_id_confirmed_by: pb.authStore.model?.id ?? null,
    espn_id_confirmed_at: new Date().toISOString(),
  }
  await pb.collection('prospects').update(p.id, { external_ids })
  return espnId
}
